import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import StaffTable from './StaffTable';
import { resetPassword, updateStatus } from '../../../services/UserService';
import { getAssignedUsers } from '../../../services/LocationService';
import { getRolesAndPermissions } from '../../../services/RoleService';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, Typography } from '@mui/material';
import EditScheduleStaffModal from './EditScheduleStaffModal';

const StaffAssignment = () => {

  const { t } = useTranslation();
  const { locationId } = useParams(); // Obtener el parámetro de la URL

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [deleteSessionPasswordModalOpen, setDeleteSessionPasswordModalOpen] = useState(false);
  const [deleteSessionStatusModalOpen, setDeleteSessionStatusModalOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [currentEmail, setCurrentEmail] = useState('');
  const [currentRole, setCurrentRole] = useState('');
  const [currentStatus, setCurrentStatus] = useState('');
  const [currentPosition, setCurrentPosition] = useState('');
  const [currentCanAccessWebDashboard, setCurrentCanAccessWebDashboard] = useState(false);
  const [currentManager, setCurrentManager] = useState('');
  const [currentScopes, setCurrentScopes] = useState([]);

  const [selectedStaff, setSelectedStaff] = useState(null);
  const [isEditScheduleModalOpen, setIsEditScheduleModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const usersData = await getAssignedUsers(locationId);
        setUsers(usersData);
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchRoles = async () => {
      try {
        const data = await getRolesAndPermissions();
        setRoles(data.roles || []); // Asegúrate de que roles siempre sea un array
      } catch (error) {
        console.error('Error fetching roles:', error);
      }
    };

    fetchUsers();
    fetchRoles();
  }, [locationId]); // Agrega locationId como una dependencia

  const handleResetPassword = async (email, newPassword, deleteSession) => {
    try {
      await resetPassword(email, newPassword, deleteSession);
    } catch (error) {
      console.error('Error resetting password:', error);
    }
  };

  const handleEditUser = async (email, roleId, statusId, positionId, deleteSession, manager, scopes, canAccessWebDashboard) => {
    try {
      const updatedUser = await updateStatus(email, roleId, statusId, positionId, deleteSession, manager, scopes, canAccessWebDashboard);
      setUsers(users.map(user =>
        user.email === email
          ? {
              ...user,
              role: updatedUser.role,
              status: updatedUser.status,
              position: updatedUser.position,
              canAccessWebDashboard: updatedUser.canAccessWebDashboard,
              manager: updatedUser.manager,
              managerId: updatedUser.manager ? updatedUser.manager.email : null,
              scopes: updatedUser.scopes || []
            }
          : user
      ));
    } catch (error) {
      console.error('Error resetting password:', error);
    }
  };

  const handleOpenSessionPasswordModal = (email, password) => {
    setCurrentEmail(email);
    setCurrentPassword(password);
    setDeleteSessionPasswordModalOpen(true);
  };

  const handleSessionPasswordModalClose = () => {
    setDeleteSessionPasswordModalOpen(false);
  };

  const handleSessionPasswordModalConfirm = (deleteSession) => {
    handleResetPassword(currentEmail, currentPassword, deleteSession);
    handleSessionPasswordModalClose();
  };

  const handleOpenSessionStatusModal = (email, data) => {
    setCurrentEmail(email);
    setCurrentRole(data.roleId);
    setCurrentStatus(data.status);
    setCurrentPosition(data.position);
    setCurrentCanAccessWebDashboard(data.canAccessWebDashboard ?? false);
    setCurrentManager(data.manager);
    setCurrentScopes(data.scopes || []);
    setDeleteSessionStatusModalOpen(true);
  };

  const handleSessionStatusModalClose = () => {
    setDeleteSessionStatusModalOpen(false);
  };

  const handleSessionStatusModalConfirm = (deleteSession) => {
    handleEditUser(currentEmail, currentRole, currentStatus, currentPosition, deleteSession, currentManager, currentScopes, currentCanAccessWebDashboard);
    handleSessionStatusModalClose();
  };

  return (
    <div>
      <StaffTable
        loading={loading}
        users={users}
        onResetPassword={(email, password) => {
          handleOpenSessionPasswordModal(email, password);
        }}
        onEditUser={(email, data) => {
          handleOpenSessionStatusModal(email, data);
        }}
        roles={roles}
        onEditSchedule={(staff) => {
          setSelectedStaff(staff);
          setIsEditScheduleModalOpen(true);
        }}
      />
      <Dialog open={deleteSessionPasswordModalOpen} onClose={handleSessionPasswordModalClose}>
        <DialogTitle>{t('confirm_logout')}</DialogTitle>
        <DialogContent>
          <Typography>{t('confirm_logout_message')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSessionPasswordModalClose}>{t('cancel')}</Button>
          <Button onClick={() => handleSessionPasswordModalConfirm(false)}>{t('no')}</Button>
          <Button onClick={() => handleSessionPasswordModalConfirm(true)} variant="contained" color="primary">{t('yes')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteSessionStatusModalOpen} onClose={handleSessionStatusModalClose}>
        <DialogTitle>{t('confirm_logout')}</DialogTitle>
        <DialogContent>
          <Typography>{t('confirm_logout_message')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSessionStatusModalClose}>{t('cancel')}</Button>
          <Button onClick={() => handleSessionStatusModalConfirm(false)}>{t('no')}</Button>
          <Button onClick={() => handleSessionStatusModalConfirm(true)} variant="contained" color="primary">{t('yes')}</Button>
        </DialogActions>
      </Dialog>

      <EditScheduleStaffModal
        open={isEditScheduleModalOpen}
        locationId={locationId}
        onClose={() => setIsEditScheduleModalOpen(false)}
        staff={selectedStaff}
        onSave={(userEmail, updatedStaff) => {
          setUsers(users.map(user => 
            user.email === userEmail 
              ? { ...user, schedule: updatedStaff.schedule } 
              : user
          ));
        }}
      />
    </div>
  );
};

export default StaffAssignment;
